import React from 'react';
import {Box, Button, List, ListItem, ListItemText, Typography} from '@mui/material';
import {ExitToApp, CallMergeTwoTone} from '@mui/icons-material';
import {useJourney} from 'api/Journeys/journey_queries';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {enqueueSnackbar} from 'notistack';
import useLocalStore from 'useLocalStore';
import { useAuth } from 'contexts/AuthContext';
import { StepTypeIcon } from 'pages/Journeys/JourneyConstants';




const ExitSidebar = ( { flowId, exitStepId, exitStepData } ) => {

    const queryClient = useQueryClient();
    const { axiosElementalApiCurrentOrg } = useAuth();

    const { data: journey, isSuccess } = useJourney(flowId);
    const [resetSidebar] = useLocalStore((state) => [state.resetSidebar]);

    console.log('exitStepData', exitStepData );

    // Function to find merge steps with their previous steps, excluding upstream merge steps
    const findMergeStepsWithPrevious = (adjacencyList) => {
        const mergeStepsWithPrevious = [];
        
        // First, find the exit step in the adjacency list
        const exitStepItem = adjacencyList.find(item => item.step.id === parseInt(exitStepId));
        if (!exitStepItem) {
            return { mergeStepsWithPrevious: [], exitPreviousStep: null }; // Exit step not found, return empty
        }
        
        // Find the step that leads to this exit step
        let exitPreviousStep = null;
        adjacencyList.forEach((value) => {
            const step = value.step;
            const nextSteps = value.next_steps || [];
            
            // Check if any next step is our target exit step
            nextSteps.forEach((nextStep) => {
                if (nextStep.step.id === parseInt(exitStepId)) {
                    exitPreviousStep = {
                        step: step,
                        edge: nextStep.edge
                    };
                }
            });
        });
        
        // Function to check if a step is upstream from the exit step
        const isUpstreamFromExit = (stepId, visited = new Set()) => {
            if (visited.has(stepId)) return false; // Prevent infinite recursion
            visited.add(stepId);
            
            // Find the step in adjacency list
            const stepItem = adjacencyList.find(item => item.step.id === stepId);
            if (!stepItem) return false;
            
            // Check if this step's next steps include the exit step
            const nextSteps = stepItem.next_steps || [];
            for (const nextStep of nextSteps) {
                if (nextStep.step.id === parseInt(exitStepId)) {
                    return true; // This step is upstream from exit
                }
                // Recursively check if any of this step's next steps lead to exit
                if (isUpstreamFromExit(nextStep.step.id, new Set(visited))) {
                    return true;
                }
            }
            return false;
        };
        
        adjacencyList.forEach((value) => {
            const step = value.step;
            if (step.type === 'merge') {
                // Skip merge steps that are upstream from the exit step
                if (isUpstreamFromExit(step.id)) {
                    return; // Skip this merge step
                }
                
                // Find steps that lead to this merge step
                const previousSteps = [];
                adjacencyList.forEach((prevValue) => {
                    const prevStep = prevValue.step;
                    const nextSteps = prevValue.next_steps || [];
                    
                    // Check if any next step is our target merge step
                    nextSteps.forEach((nextStep) => {
                        if (nextStep.step.id === step.id) {
                            previousSteps.push({
                                step: prevStep,
                                edge: nextStep.edge
                            });
                        }
                    });
                });
                
                // Find steps that come after this merge step
                const nextSteps = [];
                const mergeStepItem = adjacencyList.find(item => item.step.id === step.id);
                if (mergeStepItem && mergeStepItem.next_steps) {
                    mergeStepItem.next_steps.forEach((nextStep) => {
                        nextSteps.push({
                            step: nextStep.step,
                            edge: nextStep.edge
                        });
                    });
                }
                
                mergeStepsWithPrevious.push({
                    mergeStep: step,
                    previousSteps: previousSteps,
                    nextSteps: nextSteps
                });
            }
        });
        return { mergeStepsWithPrevious, exitPreviousStep };
    };

    // get all the merge steps with their previous steps
    const {data: mergeData, isLoading: isLoadingMergeSteps} = useQuery({
        queryKey: ['journeys', journey?.id, 'adjacencyList', 'draft'],
        queryFn: async () => {
            return axiosElementalApiCurrentOrg.get(`/flows/${journey.id}/draft/adjacency_list`);
        },
        select: findMergeStepsWithPrevious,
        enabled: !!journey?.id
    });

    const mergeStepsWithPrevious = mergeData?.mergeStepsWithPrevious || [];
    const exitPreviousStep = mergeData?.exitPreviousStep;

    const mutationMergeExit = useMutation({
        mutationFn: (mergeStepId) => {
            return axiosElementalApiCurrentOrg.put(`/flows/${flowId}/${mergeStepId}/merge/${exitStepId}`,{});
        },
        onSuccess: () => {
            // queryClient.invalidateQueries({ queryKey: ['journeys', journey?.id, 'adjacencyList', 'draft'] } );
            queryClient.invalidateQueries({ queryKey: ['journeys', journey?.id] });
            enqueueSnackbar('Exit step merged successfully');
            resetSidebar();
        }
    });

    const handleMerge = (mergeStepId) => {
        mutationMergeExit.mutate(mergeStepId);
    };

    if(!isSuccess){
        return <div>Loading or error</div>;
    }

    return (
        <div className='flow-sidebar' style={{width: '15vw'}}>
            <div className='flow-sidebar__header'>
                <Typography component='div' variant='body1' className='flow-sidebar_title no-edit'>
                    <ExitToApp />
                    Journey Exit
                </Typography>
            </div>
            <Box className='flow-sidebar__body' p={0}>
                <Box className='flow-sidebar__body-content'>
                    <Box display='flex' flexDirection='column'>
                        <Box display='flex' flexDirection='column' p={1} className='merge__step'>

                            {exitPreviousStep && (
                                <Box mb={2}>
                                    <Typography variant="h6" gutterBottom>
                                        Current Exit Path
                                    </Typography>
                                    <Box display="flex" alignItems="center" sx={{ 
                                        border: '1px solid #e0e0e0', 
                                        borderRadius: 1, 
                                        p: 1,
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        <Box display="flex" alignItems="center">
                                            <StepTypeIcon step={exitPreviousStep.step} fontSize="small" color={undefined} />
                                            <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                                                {exitPreviousStep.step.description || `Step ${exitPreviousStep.step.id}`}
                                                {exitPreviousStep.edge.key_from && ` (${exitPreviousStep.edge.key_from})`}
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                                                (prev)
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ mx: 1 }}>
                                            →
                                        </Typography>
                                        <Box display="flex" alignItems="center">
                                            <ExitToApp fontSize="small" />
                                            <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'bold', color: 'secondary.main' }}>
                                                Exit
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'bold', color: 'secondary.main' }}>
                                                (this step)
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {mergeStepsWithPrevious && mergeStepsWithPrevious.length > 0 && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Connect Exit to ...
                                </Typography>

                                {isLoadingMergeSteps ? (
                                    <Typography variant="body2" color="textSecondary">
                                        Loading merge steps...
                                    </Typography>
                                ) : (
                                    <List dense>
                                        {mergeStepsWithPrevious.map(({ mergeStep, previousSteps, nextSteps }) => (
                                            <ListItem
                                                key={mergeStep.id}
                                                sx={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    backgroundColor: '#fafafa'
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        previousSteps.length > 0 ? (
                                                            <Box display="flex" alignItems="center">
                                                                <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                                                                    {previousSteps.map((p, index) => (
                                                                        <Box key={p.step.id} display="flex" alignItems="center" sx={{ mr: index < previousSteps.length - 1 ? 1 : 0 }}>
                                                                            <StepTypeIcon step={p.step} fontSize="small" color={undefined} />
                                                                            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'primary.main', ml: 0.5 }}>
                                                                                {p.step.description || `Step ${p.step.id}`}
                                                                            </Typography>
                                                                            {p.edge.key_from && (
                                                                                <Typography variant="body2" component="span" sx={{ ml: 0.5, fontWeight: 'bold', color: 'info.main' }}>
                                                                                    ({p.edge.key_from})
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                                <Typography variant="body2" component="span" sx={{ mx: 1 }}>
                                                                    →
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="secondary"
                                                                    startIcon={<CallMergeTwoTone style={{ transform: 'rotate(180deg)' }} />}
                                                                    onClick={() => handleMerge(mergeStep.id)}
                                                                    disabled={mutationMergeExit.isLoading}
                                                                    sx={{ ml: 1 }}
                                                                >
                                                                    Connect Here
                                                                </Button>
                                                                {nextSteps && nextSteps.length > 0 && (
                                                                    <>
                                                                        <Typography variant="body2" component="span" sx={{ mx: 1 }}>
                                                                            →
                                                                        </Typography>
                                                                        <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                                                                            {nextSteps.map((nextStep, index) => (
                                                                                <Box key={nextStep.step.id} display="flex" alignItems="center" sx={{ mr: index < nextSteps.length - 1 ? 1 : 0 }}>
                                                                                    <StepTypeIcon step={nextStep.step} fontSize="small" color={undefined} />
                                                                                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'primary.main', ml: 0.5 }}>
                                                                                        {nextStep.step.description || `Step ${nextStep.step.id}`}
                                                                                    </Typography>
                                                                                    {nextStep.edge.key_from && (
                                                                                        <Typography variant="body2" component="span" sx={{ ml: 0.5, fontWeight: 'bold', color: 'info.main' }}>
                                                                                            ({nextStep.edge.key_from})
                                                                                        </Typography>
                                                                                    )}
                                                                                </Box>
                                                                            ))}
                                                                        </Box>
                                                                    </>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            mergeStep.description || mergeStep.label || `Merge Step ${mergeStep.id}`
                                                        )
                                                    }

                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Box>
                        )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </div>
    );
};

export default ExitSidebar;
